import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

const STORAGE_KEY = 'dictation-widget-position'
const DRAG_THRESHOLD = 5 // px — movement below this is treated as a click, not a drag

export const PILL_WIDTH = 300
export const PILL_HEIGHT = 52

interface Position {
  x: number
  y: number
}

const getDefaultPosition = (width: number, height: number): Position => ({
  x: window.innerWidth - width - 24,
  y: window.innerHeight - height - 24,
})

const clampPosition = (pos: Position, width: number, height: number): Position => ({
  x: Math.max(0, Math.min(Math.max(0, window.innerWidth - width), pos.x)),
  y: Math.max(0, Math.min(Math.max(0, window.innerHeight - height), pos.y)),
})

const getSavedPosition = (width: number, height: number): Position | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Number.isFinite(parsed?.x) || !Number.isFinite(parsed?.y)) return null
    return clampPosition(parsed, width, height)
  } catch {}
  return null
}

export const useDraggablePosition = (
  defaultWidth = PILL_WIDTH,
  defaultHeight = PILL_HEIGHT,
  isRendered = true,
) => {
  const [position, setPosition] = useState<Position>(
    () =>
      getSavedPosition(defaultWidth, defaultHeight) ??
      getDefaultPosition(defaultWidth, defaultHeight),
  )
  const positionRef = useRef(position)
  const dimsRef = useRef({ width: defaultWidth, height: defaultHeight })
  const isDraggingRef = useRef(false)
  const hasMovedRef = useRef(false)
  const offsetRef = useRef<Position>({ x: 0, y: 0 })
  const startClientRef = useRef<Position>({ x: 0, y: 0 })
  const widgetRef = useRef<HTMLDivElement>(null)

  const clampAndApply = useCallback((width: number, height: number) => {
    dimsRef.current = { width, height }
    const clamped = clampPosition(positionRef.current, width, height)
    if (clamped.x !== positionRef.current.x || clamped.y !== positionRef.current.y) {
      positionRef.current = clamped
      setPosition(clamped)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(clamped))
    }
  }, [])

  // Re-run when the widget actually mounts: the first effect pass often sees a null ref
  // because DictationWidget returns null until feature flags are loaded.
  useLayoutEffect(() => {
    if (!isRendered) return
    const el = widgetRef.current
    if (!el) return
    clampAndApply(el.offsetWidth || defaultWidth, el.offsetHeight || defaultHeight)
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const { width, height } = entry.contentRect
      clampAndApply(width, height)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [clampAndApply, defaultHeight, defaultWidth, isRendered])

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isDraggingRef.current = true
    hasMovedRef.current = false
    startClientRef.current = { x: e.clientX, y: e.clientY }
    offsetRef.current = {
      x: e.clientX - positionRef.current.x,
      y: e.clientY - positionRef.current.y,
    }
  }, [])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return
      const dx = e.clientX - startClientRef.current.x
      const dy = e.clientY - startClientRef.current.y
      if (!hasMovedRef.current && Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return
      hasMovedRef.current = true
      const { width: w, height: h } = dimsRef.current
      const newPos: Position = {
        x: Math.max(0, Math.min(window.innerWidth - w, e.clientX - offsetRef.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - h, e.clientY - offsetRef.current.y)),
      }
      positionRef.current = newPos
      setPosition(newPos)
    }

    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false
        localStorage.setItem(STORAGE_KEY, JSON.stringify(positionRef.current))
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  useEffect(() => {
    const handleResize = () => {
      const { width: w, height: h } = dimsRef.current
      clampAndApply(w, h)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [clampAndApply])

  const wasDragged = useCallback(() => hasMovedRef.current, [])
  const resetDrag = useCallback(() => {
    hasMovedRef.current = false
  }, [])

  return { position, onMouseDown, wasDragged, resetDrag, widgetRef }
}
